import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  Tooltip,
  LinearProgress,
  Fade
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Description as DocumentIcon,
  InsertDriveFile as FileIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  Article as DocIcon
} from '@mui/icons-material';
import { apiUrl } from '../utils/api';

interface PODocumentsProps {
  poNumber: string;
  projectId: number;
  canWrite: boolean;
  onDocumentCountChange?: (count: number) => void;
}

interface Document {
  document_id: number;
  document_name: string;
  document_type: string;
  file_path: string;
  file_size: number;
  mime_type: string;
  version: number;
  notes: string | null;
  created_at: string;
  uploaded_by_name?: string;
}

const PODocuments: React.FC<PODocumentsProps> = ({
  poNumber,
  projectId,
  canWrite,
  onDocumentCountChange
}) => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const [uploadData, setUploadData] = useState({
    document_type: 'catalog',
    notes: ''
  });

  const documentTypes = [
    { value: 'catalog', label: 'Catalog', icon: '📘', formats: 'pdf, jpg, jpeg, png' },
    { value: 'datasheet', label: 'Data Sheet', icon: '📊', formats: 'pdf, doc, docx, xlsx, xls' },
    { value: 'specification', label: 'Specification', icon: '📋', formats: 'pdf, doc, docx' },
    { value: 'drawing', label: 'Drawing', icon: '📐', formats: 'pdf, dwg, dxf, jpg, jpeg, png' },
    { value: 'correspondence', label: 'Correspondence', icon: '📧', formats: 'pdf, doc, docx, msg, eml' },
    { value: 'invoice', label: 'Invoice', icon: '💰', formats: 'pdf, xlsx, xls' },
    { value: 'other', label: 'Other', icon: '📎', formats: 'pdf, doc, docx, xlsx, xls, jpg, jpeg, png, zip, rar' }
  ];

  const fetchDocuments = useCallback(async (notifyParent: boolean = false) => {
    if (!poNumber) return;

    try {
      setLoading(true);
      const response = await fetch(
        apiUrl(`/api/purchase-orders/${poNumber}/documents`),
        { credentials: 'include' }
      );

      if (response.ok) {
        const data = await response.json();
        const docs = data.documents || [];
        setDocuments(docs);
        if (notifyParent) {
          onDocumentCountChange?.(docs.length);
        }
      } else {
        const data = await response.json();
        throw new Error(data.error || 'Failed to fetch documents');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [poNumber, onDocumentCountChange]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleUploadDialogOpen = () => {
    setUploadDialogOpen(true);
    setUploadData({
      document_type: 'catalog',
      notes: ''
    });
    setSelectedFile(null);
    setUploadProgress(0);
  };

  const handleUploadDialogClose = () => {
    setUploadDialogOpen(false);
    setSelectedFile(null);
    setUploadProgress(0);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setSelectedFile(event.target.files[0]);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setSelectedFile(e.dataTransfer.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || uploading) return;

    try {
      setUploading(true);
      setUploadProgress(0);

      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('document_type', uploadData.document_type);
      if (uploadData.notes) formData.append('notes', uploadData.notes);

      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      const response = await fetch(
        apiUrl(`/api/purchase-orders/${poNumber}/documents`),
        {
          method: 'POST',
          credentials: 'include',
          body: formData
        }
      );

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (response.ok) {
        await fetchDocuments(true);
        handleUploadDialogClose();
        setSuccess('Document uploaded successfully to Azure');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        const data = await response.json();
        throw new Error(data.error || 'Failed to upload document');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (document: Document) => {
    try {
      // If it's an Azure URL, open directly
      if (document.file_path && document.file_path.includes('blob.core.windows.net')) {
        window.open(document.file_path, '_blank');
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDelete = async (documentId: number) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      setDeletingId(documentId);
      const response = await fetch(
        apiUrl(`/api/purchase-orders/${poNumber}/documents/${documentId}`),
        {
          method: 'DELETE',
          credentials: 'include'
        }
      );

      if (response.ok) {
        await fetchDocuments(true);
        setSuccess('Document deleted successfully');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete document');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setDeletingId(null);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (!bytes) return 'N/A';
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    const mb = kb / 1024;
    return `${mb.toFixed(2)} MB`;
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-SG', {
      year: 'numeric',
      month: 'short',
      day: '2-digit'
    });
  };

  const getFileIcon = (mimeType: string) => {
    if (mimeType?.includes('pdf')) return <PdfIcon sx={{ color: '#D32F2F' }} />;
    if (mimeType?.includes('image')) return <ImageIcon sx={{ color: '#1976D2' }} />;
    if (mimeType?.includes('word') || mimeType?.includes('document')) return <DocIcon sx={{ color: '#1565C0' }} />;
    return <FileIcon sx={{ color: '#757575' }} />;
  };

  const getTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      catalog: '#2196F3',
      datasheet: '#9C27B0',
      specification: '#FF9800',
      drawing: '#4CAF50',
      correspondence: '#00BCD4',
      invoice: '#E91E63',
      other: '#607D8B'
    };
    return colors[type] || '#607D8B';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6" fontWeight={600}>
          Purchase Order Documents
        </Typography>
        {canWrite && (
          <Button
            variant="contained"
            startIcon={<UploadIcon />}
            onClick={handleUploadDialogOpen}
            sx={{ borderRadius: 2 }}
          >
            Upload Document
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Fade in>
          <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
            {success}
          </Alert>
        </Fade>
      )}

      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 600, py: 1.5 }}>File</TableCell>
                <TableCell sx={{ fontWeight: 600, py: 1.5 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 600, py: 1.5 }}>Size</TableCell>
                <TableCell sx={{ fontWeight: 600, py: 1.5 }}>Uploaded</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, py: 1.5 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {documents.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <DocumentIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                    <Typography color="text.secondary" variant="body2">
                      No documents attached yet
                    </Typography>
                    {canWrite && (
                      <Button
                        size="small"
                        startIcon={<UploadIcon />}
                        onClick={handleUploadDialogOpen}
                        sx={{ mt: 1, textTransform: 'none' }}
                      >
                        Upload your first document
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                documents.map((doc) => (
                  <TableRow
                    key={doc.document_id}
                    sx={{
                      '&:hover': { bgcolor: 'action.hover' },
                      '&:last-child td': { borderBottom: 0 }
                    }}
                  >
                    <TableCell sx={{ py: 1.5 }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        {getFileIcon(doc.mime_type)}
                        <Box>
                          <Typography variant="body2" fontWeight={500} noWrap sx={{ maxWidth: 180 }}>
                            {doc.document_name}
                          </Typography>
                          {doc.notes && (
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 180, display: 'block' }}>
                              {doc.notes}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      <Chip
                        label={doc.document_type}
                        size="small"
                        sx={{
                          bgcolor: `${getTypeColor(doc.document_type)}15`,
                          color: getTypeColor(doc.document_type),
                          fontWeight: 500,
                          fontSize: '0.75rem'
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        {formatFileSize(doc.file_size)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(doc.created_at)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ py: 1.5 }}>
                      <Box display="flex" gap={0.5} justifyContent="flex-end">
                        <Tooltip title="Download">
                          <IconButton
                            onClick={() => handleDownload(doc)}
                            size="small"
                            color="primary"
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {canWrite && (
                          <Tooltip title="Delete">
                            <IconButton
                              onClick={() => handleDelete(doc.document_id)}
                              size="small"
                              color="error"
                              disabled={deletingId === doc.document_id}
                            >
                              {deletingId === doc.document_id ? (
                                <CircularProgress size={18} color="error" />
                              ) : (
                                <DeleteIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Upload Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={handleUploadDialogClose}
        maxWidth="sm"
        fullWidth
        slotProps={{ paper: { sx: { borderRadius: 3 } } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <UploadIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>Upload PO Document</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
            {/* Drag & Drop Zone */}
            <Box
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              sx={{
                border: '2px dashed',
                borderColor: dragActive ? 'primary.main' : 'grey.300',
                borderRadius: 2,
                p: 3,
                textAlign: 'center',
                bgcolor: dragActive ? 'primary.50' : 'grey.50',
                transition: 'all 0.2s ease',
                cursor: 'pointer',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'primary.50'
                }
              }}
              onClick={() => document.getElementById('po-file-input')?.click()}
            >
              <input
                id="po-file-input"
                type="file"
                hidden
                onChange={handleFileSelect}
              />
              {selectedFile ? (
                <Box>
                  <FileIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                  <Typography variant="body1" fontWeight={500}>
                    {selectedFile.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatFileSize(selectedFile.size)}
                  </Typography>
                </Box>
              ) : (
                <Box>
                  <UploadIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                  <Typography variant="body1" color="text.secondary">
                    Drag & drop file here or click to browse
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    Catalog, Data sheet, Specification, etc. (max 10MB)
                  </Typography>
                </Box>
              )}
            </Box>

            {uploading && (
              <Box>
                <LinearProgress
                  variant="determinate"
                  value={uploadProgress}
                  sx={{ borderRadius: 1, height: 6 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  Uploading to Azure... {uploadProgress}%
                </Typography>
              </Box>
            )}

            <TextField
              select
              label="Document Type"
              value={uploadData.document_type}
              onChange={(e) => setUploadData({ ...uploadData, document_type: e.target.value })}
              fullWidth
              size="small"
              helperText={`Supported formats: ${documentTypes.find(t => t.value === uploadData.document_type)?.formats || ''}`}
            >
              {documentTypes.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <span>{type.icon}</span>
                    {type.label}
                  </Box>
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Notes (optional)"
              value={uploadData.notes}
              onChange={(e) => setUploadData({ ...uploadData, notes: e.target.value })}
              fullWidth
              multiline
              rows={2}
              size="small"
              placeholder="Add any notes or description..."
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 1 }}>
          <Button onClick={handleUploadDialogClose} disabled={uploading}>
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={!selectedFile || uploading}
            startIcon={uploading ? <CircularProgress size={16} /> : <UploadIcon />}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PODocuments;
